import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Fragment, useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { company } from '~/company/Company';
import CategoryBlock from '~/components/CategoryBlocks/CategoryBlock';
import ShopCategoriesCarousel from '~/components/CategoryBlocks/ShopCategoriesCarousel';
import { BannerMainBlockSkeleton as BannerMainBlockSkeletonV3 } from '~/components/isolated/CategoryBlocks/BannerMainBlock';
import { ProductsCarouselSliderOneBlockSkeleton as ProductsCarouselSliderOneBlockSkeletonV3 } from '~/components/isolated/CategoryBlocks/ProductsCarouselSliderOneBlock';
import RecommendationSection, {
  RecommendationSliderSkeleton,
} from '~/components/isolated/CategoryBlocks/RecommendationSection';
import {
  HeroBanner,
  HeroBannerSkeleton,
} from '~/components/isolated/HeroBanner';
import { ModalType } from '~/components/Modal/interface';
import { NoSsr } from '~/components/NoSsr';
import { useModal } from '~/hooks/useModal';
import { useStores } from '~/hooks/useStore';
import { ClubHomeBanner } from '~/modules/membership/ui/club/ClubEntryPoints';
import { TrustPanel } from '~/pages/DubaiExpress/components/TrustPanel';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { orderStore } from '~/stores';
import {
  ORDER_AGAIN_CONFIG,
  orderAgainStore,
  TOP_PICKS_CONFIG,
  topPicksStore,
} from '~/stores/RecommendationsStore';
import { checkAndSaveReferralPromocode } from '~/utils/referralLink';

import styles from './MainV3.module.scss';
import { ScrollPaginationSkeleton } from './ScrollPaginationSkeleton';

const MainV3 = () => {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const { state, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { homeCategories, shopCategories, heroBanner } = useStores();

  useEffect(() => {
    if (state?.isShowAuthModal) {
      openModal(ModalType.Auth);
    } else if (state?.isShowDeliveryModal) {
      openModal(ModalType.Auth, 'auth', {}, true);
    }
  }, [state?.isShowAuthModal, state?.isShowDeliveryModal]);

  useEffect(() => {
    (async () => {
      analyticsService.track(AnalyticsEvent.CATALOG_HOME_PAGE_VISITED, {
        warehouse_code: orderStore.etaWarehouseCode,
      });

      const urlSearchParams = await checkAndSaveReferralPromocode(search);

      if (urlSearchParams) {
        navigate(
          {
            pathname,
            search: urlSearchParams.toString(),
          },
          { replace: true },
        );
      }
    })();
  }, []);

  const loadMore = useCallback(
    () =>
      homeCategories.paginationRequest.loadMore(orderStore.etaWarehouseCode),
    [],
  );

  const skeleton = (
    <>
      <BannerMainBlockSkeletonV3 />
      <ProductsCarouselSliderOneBlockSkeletonV3 />
      <RecommendationSliderSkeleton />
      <RecommendationSliderSkeleton />
      <ProductsCarouselSliderOneBlockSkeletonV3 />
    </>
  );

  return (
    <>
      <Helmet>
        <title>{t('phrases:mainMetaTitle')}</title>
        <meta name="description" content={t('phrases:mainMetaDescription')} />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {heroBanner.shouldShowSection &&
        (heroBanner.shouldShowSkeleton ? (
          <HeroBannerSkeleton />
        ) : (
          <HeroBanner items={heroBanner.items} />
        ))}
      <div
        className={classes([
          styles.root,
          heroBanner.shouldShowSection && styles.withOverlap,
        ])}
        data-company={company.name}
      >
        <div className={styles.container}>
          <ClubHomeBanner />
          {company.is('dubaiexpress') && (
            <div className={styles.category} data-company={company.name}>
              <TrustPanel />
            </div>
          )}
          {company.is('mcosmetic') && shopCategories.list.length > 0 && (
            <ShopCategoriesCarousel shopCategories={shopCategories} />
          )}
          {homeCategories.list.length === 0
            ? skeleton
            : homeCategories.list
                .filter((category) => category.subcategory.length)
                .map((category, index) => {
                  const isSecondCategory = index === 1;

                  return (
                    <Fragment key={category.id}>
                      <div
                        className={styles.category}
                        data-company={company.name}
                      >
                        <CategoryBlock
                          category={category}
                          externalLink={category.externalLink ?? undefined}
                          priority={
                            index === 0 && !heroBanner.shouldShowSection
                          }
                        />
                      </div>
                      {isSecondCategory && (
                        <>
                          <RecommendationSection
                            store={orderAgainStore}
                            config={ORDER_AGAIN_CONFIG}
                            className={styles.category}
                            data-company={company.name}
                          />
                          <RecommendationSection
                            store={topPicksStore}
                            config={TOP_PICKS_CONFIG}
                            className={styles.category}
                            data-company={company.name}
                          />
                        </>
                      )}
                    </Fragment>
                  );
                })}
          <NoSsr>
            {homeCategories.list.length > 0 &&
              homeCategories.paginationRequest.canLoadMore && (
                <ScrollPaginationSkeleton
                  className={styles.scrollPaginationSkeleton}
                  onLoadMore={loadMore}
                />
              )}
          </NoSsr>
        </div>
      </div>
    </>
  );
};

export default observer(MainV3);
